import { useSyncExternalStore } from 'react';

/**
 * The whole router.
 *
 * The site has three pages behind the door — the board, the corkboard and the
 * desk — and that is not enough to earn react-router: a dependency, a rewrite of App's
 * door gate, and a second mental model for a site whose navigation is "home,
 * and the other one". This is thirty lines and no dependency.
 *
 * **Hash and not history**, deliberately. Caddy serves the SPA as static files
 * with no rewrite rule; a real path would 404 on a hard refresh unless the
 * server learned to send index.html for every unknown URL. A hash needs nothing
 * from the server, survives a refresh, and is still a URL you can paste into
 * the group chat — which is the point of the corkboard having its own page at
 * all.
 *
 * Deep links survive the door for free. `App` gates on `entered`, which is per
 * page load; nothing in the door touches the hash, so somebody opening
 * `#/predlozi` cold meets the bouncers first and lands on the corkboard after.
 *
 * The admin panel is still an overlay. It is one person's surface, it is not
 * something anybody links to, and it would gain nothing but a URL.
 */

export const ROUTES = {
  board: '#/',
  /** Serbian, like every other user-facing string. */
  pitch: '#/predlozi',
  /** Your ticket collection — the messy desk (DESIGN.md §10). */
  desk: '#/sto',
} as const;

export type RouteName = keyof typeof ROUTES;

export function parseRoute(hash: string): RouteName {
  // Everything unknown is the board — a typo in a pasted link should land
  // somewhere real rather than on an error page this site does not have.
  if (hash === ROUTES.pitch) return 'pitch';
  if (hash === ROUTES.desk) return 'desk';
  return 'board';
}

const subscribe = (onChange: () => void): (() => void) => {
  window.addEventListener('hashchange', onChange);
  return () => window.removeEventListener('hashchange', onChange);
};

/**
 * `useSyncExternalStore` rather than `useState` + an effect: the hash can
 * change before React has mounted the listener (the browser applies the hash
 * from the address bar immediately), and this is the hook that exists to close
 * exactly that gap.
 */
export function useRoute(): RouteName {
  const hash = useSyncExternalStore(
    subscribe,
    () => window.location.hash,
    () => ROUTES.board,
  );
  return parseRoute(hash);
}

/**
 * Go somewhere from code.
 *
 * Links are `<a href>` wherever a link will do — middle-click and
 * open-in-new-tab are free that way, and the browser's own affordances are
 * better than any we would write. This is for the handful of places that are
 * not a click on a link: logging out, mostly.
 */
export function navigate(route: RouteName): void {
  window.location.hash = ROUTES[route];
}
